<script setup lang="ts">
  import { toRef } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import ChevronLeftIcon from '@/shared/assets/icons/ChevronLeftIcon.svg';
  import ChevronRightIcon from '@/shared/assets/icons/ChevronRightIcon.svg';
  import { IconImage } from '@/shared/ui';
  import { WEEKDAYS, useDateRangePicker } from '@/shared/lib';

  const MONTHS = [
    'JAN',
    'FEV',
    'MAR',
    'ABR',
    'MAI',
    'JUN',
    'JUL',
    'AGO',
    'SET',
    'OUT',
    'NOV',
    'DEZ',
  ];

  const props = withDefaults(
    defineProps<{
      startDate: Date | null;
      endDate: Date | null;
      isOpen: boolean;
    }>(),
    {}
  );

  const emit = defineEmits([
    'update:startDate',
    'update:endDate',
    'update:isOpen',
    'apply',
    'cancel',
  ]);

  const {
    tempStartDate,
    tempEndDate,
    viewMode,
    calendarDays,
    yearsRange,
    headerTitle,
    canApply,
    isDateDisabled,
    isDateSelected,
    isRangeStart,
    isRangeEnd,
    isMonthSelected,
    isYearSelected,
    selectDate,
    selectMonth,
    selectYear,
    toggleViewMode,
    handlePrev,
    handleNext,
    resetToProps,
  } = useDateRangePicker({
    startDate: toRef(props, 'startDate'),
    endDate: toRef(props, 'endDate'),
    isOpen: toRef(props, 'isOpen'),
  });

  const applyRange = () => {
    emit('update:startDate', tempStartDate.value);
    emit('update:endDate', tempEndDate.value);
    emit('apply', { startDate: tempStartDate.value, endDate: tempEndDate.value });
    emit('update:isOpen', false);
  };

  const cancelSelection = () => {
    resetToProps();
    emit('cancel');
    emit('update:isOpen', false);
  };
</script>

<template>
  <VueFinalModal
    :model-value="isOpen"
    class="flex justify-center items-center"
    content-class="bg-white-01 rounded-lg shadow-lg w-[360px] overflow-hidden"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @update:model-value="$emit('update:isOpen', $event)"
  >
    <div class="datepicker-container">
      <div class="flex justify-between items-center p-4 border-b border-gray-02">
        <button
          class="p-2"
          @click="handlePrev"
        >
          <IconImage
            :src="ChevronLeftIcon"
            alt="Anterior"
            class="w-5 h-5"
          />
        </button>
        <button
          class="text-lg font-medium capitalize hover:text-[var(--theme-secondary-ink)]"
          @click="toggleViewMode"
        >
          {{ headerTitle }}
        </button>
        <button
          class="p-2"
          @click="handleNext"
        >
          <IconImage
            :src="ChevronRightIcon"
            alt="Próximo"
            class="w-5 h-5"
          />
        </button>
      </div>

      <div class="p-4">
        <template v-if="viewMode === 'days'">
          <div class="grid grid-cols-7 mb-2">
            <div
              v-for="day in WEEKDAYS"
              :key="day"
              class="text-center text-sm font-medium text-gray-07"
            >
              {{ day }}
            </div>
          </div>

          <div class="grid grid-cols-7 gap-1">
            <div
              v-for="(day, index) in calendarDays()"
              :key="index"
              class="w-10 h-10 flex items-center justify-center text-sm transition-colors rounded-full"
              :class="{
                'cursor-not-allowed opacity-40': isDateDisabled(day.date),
                'cursor-pointer': !isDateDisabled(day.date),
                'text-gray-02': !day.isCurrentMonth && !isDateDisabled(day.date),
                'text-gray-07':
                  day.isCurrentMonth && !isDateSelected(day.date) && !isDateDisabled(day.date),
                'bg-[color-mix(in_srgb,var(--color-white-01),var(--theme-secondary)_20%)]':
                  isDateSelected(day.date) && !isRangeStart(day.date) && !isRangeEnd(day.date),
                'bg-[var(--theme-secondary)] text-white font-medium':
                  isRangeStart(day.date) || isRangeEnd(day.date),
              }"
              @click="selectDate(day.date)"
            >
              {{ day.date.getDate() }}
            </div>
          </div>
        </template>

        <template v-else-if="viewMode === 'months'">
          <div class="grid grid-cols-4 gap-2">
            <button
              v-for="(month, index) in MONTHS"
              :key="month"
              class="px-3 py-3 rounded-lg text-sm font-medium transition-colors"
              :class="{
                'bg-[var(--theme-secondary)] text-white': isMonthSelected(index),
                'text-gray-07 hover:bg-gray-01': !isMonthSelected(index),
              }"
              @click="selectMonth(index)"
            >
              {{ month }}
            </button>
          </div>
        </template>

        <template v-else>
          <div class="flex flex-col gap-2">
            <button
              v-for="year in yearsRange"
              :key="year"
              class="px-4 py-3 rounded-lg text-sm font-medium transition-colors"
              :class="{
                'bg-[var(--theme-secondary)] text-white': isYearSelected(year),
                'text-gray-07 hover:bg-gray-01': !isYearSelected(year),
              }"
              @click="selectYear(year)"
            >
              {{ year }}
            </button>
          </div>
        </template>
      </div>

      <div class="flex justify-end gap-2.5 p-4 border-t border-gray-02">
        <button
          class="px-4 py-3 border border-gray-03 text-gray-03 rounded-lg font-medium"
          @click="cancelSelection"
        >
          Cancelar
        </button>
        <button
          class="px-4 py-3 bg-[var(--theme-secondary)] text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!canApply"
          @click="applyRange"
        >
          Salvar
        </button>
      </div>
    </div>
  </VueFinalModal>
</template>
